import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {
  CODING_BLOCKS,
  CODING_CATEGORIES,
  DEFAULT_CODING_PROGRAM_NAME,
  allCodingBlocks,
  codingBlockDefinition,
  codingCommandCount,
  codingProgramEstimatedSeconds,
  codingProgramPreviewSequence,
  codingProgramToPython,
  createCodingBlock,
  createDefaultCodingProgram,
  type CodingCategoryId,
  type CodingProgramBlock,
} from '../domain/coding'
import { orderedCubes } from '../domain/motor-control'
import { useStudioStore } from '../store/useStudioStore'
import { Icon } from './Icon'

type CodingContextValue = {
  blocks: CodingProgramBlock[]
  selectedBlockId: string | null
  addBlock: (type: string, parentId?: string) => void
  selectBlock: (id: string) => void
  updateBlock: (id: string, key: string, value: string | number) => void
  duplicateBlock: (id: string) => void
  removeBlock: (id: string) => void
  moveBlock: (id: string, delta: -1 | 1) => void
  reorderBlock: (id: string, targetId: string) => void
  nestBlock: (id: string, parentId: string) => void
  moveBlockToRoot: (id: string) => void
  clearProgram: () => void
}

const CodingContext = createContext<CodingContextValue | null>(null)
let blockSequence = 0

function nextBlockId() {
  blockSequence += 1
  return `coding-block-${Date.now()}-${blockSequence}`
}

function findBlock(blocks: CodingProgramBlock[], id: string): CodingProgramBlock | null {
  for (const block of blocks) {
    if (block.id === id) return block
    const child = findBlock(block.children, id)
    if (child) return child
  }
  return null
}

function updateBlockTree(
  blocks: CodingProgramBlock[],
  id: string,
  update: (block: CodingProgramBlock) => CodingProgramBlock,
): CodingProgramBlock[] {
  return blocks.map((block) => block.id === id
    ? update(block)
    : { ...block, children: updateBlockTree(block.children, id, update) })
}

function removeBlockTree(
  blocks: CodingProgramBlock[],
  id: string,
): { blocks: CodingProgramBlock[]; removed: CodingProgramBlock | null } {
  const directIndex = blocks.findIndex((block) => block.id === id)
  if (directIndex >= 0) {
    return {
      blocks: [...blocks.slice(0, directIndex), ...blocks.slice(directIndex + 1)],
      removed: blocks[directIndex],
    }
  }
  let removed: CodingProgramBlock | null = null
  const next = blocks.map((block) => {
    if (removed) return block
    const result = removeBlockTree(block.children, id)
    removed = result.removed
    return result.removed ? { ...block, children: result.blocks } : block
  })
  return { blocks: next, removed }
}

function moveWithinTree(blocks: CodingProgramBlock[], id: string, delta: -1 | 1): CodingProgramBlock[] {
  const index = blocks.findIndex((block) => block.id === id)
  if (index >= 0) {
    const nextIndex = index + delta
    const minimum = blocks[0]?.type === 'when-start' ? 1 : 0
    if (nextIndex < minimum || nextIndex >= blocks.length) return blocks
    const next = [...blocks]
    const [moved] = next.splice(index, 1)
    next.splice(nextIndex, 0, moved)
    return next
  }
  return blocks.map((block) => ({ ...block, children: moveWithinTree(block.children, id, delta) }))
}

function reorderWithinTree(blocks: CodingProgramBlock[], id: string, targetId: string): CodingProgramBlock[] {
  const sourceIndex = blocks.findIndex((block) => block.id === id)
  const targetIndex = blocks.findIndex((block) => block.id === targetId)
  if (sourceIndex >= 0 && targetIndex >= 0) {
    const minimum = blocks[0]?.type === 'when-start' ? 1 : 0
    if (sourceIndex < minimum || targetIndex < minimum || sourceIndex === targetIndex) return blocks
    const next = [...blocks]
    const [moved] = next.splice(sourceIndex, 1)
    next.splice(targetIndex, 0, moved)
    return next
  }
  return blocks.map((block) => ({
    ...block,
    children: reorderWithinTree(block.children, id, targetId),
  }))
}

function insertDuplicate(
  blocks: CodingProgramBlock[],
  id: string,
  duplicate: CodingProgramBlock,
): CodingProgramBlock[] {
  const index = blocks.findIndex((block) => block.id === id)
  if (index >= 0) return [...blocks.slice(0, index + 1), duplicate, ...blocks.slice(index + 1)]
  return blocks.map((block) => ({ ...block, children: insertDuplicate(block.children, id, duplicate) }))
}

function cloneBlock(block: CodingProgramBlock): CodingProgramBlock {
  return {
    ...block,
    id: nextBlockId(),
    values: { ...block.values },
    children: block.children.map(cloneBlock),
  }
}

function CodingProvider({ children }: { children: ReactNode }) {
  const blocks = useStudioStore((state) => state.codingProgram)
  const setBlocks = useStudioStore((state) => state.setCodingProgram)
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>('coding-start')

  const value = useMemo<CodingContextValue>(() => ({
    blocks,
    selectedBlockId,
    addBlock: (type, parentId) => {
      const definition = codingBlockDefinition(type)
      const existingStart = definition.type === 'when-start'
        ? allCodingBlocks(blocks).find((block) => block.type === 'when-start')
        : null
      if (existingStart) {
        setSelectedBlockId(existingStart.id)
        return
      }
      const block = createCodingBlock(type, nextBlockId())
      setBlocks((current) => {
        if (parentId && definition.category !== 'event') {
          return updateBlockTree(current, parentId, (parent) => (
            codingBlockDefinition(parent.type).container
              ? { ...parent, children: [...parent.children, block] }
              : parent
          ))
        }
        if (definition.category !== 'event') return [...current, block]
        const firstCommand = current.findIndex((item) => codingBlockDefinition(item.type).category !== 'event')
        if (firstCommand < 0) return [...current, block]
        return [...current.slice(0, firstCommand), block, ...current.slice(firstCommand)]
      })
      setSelectedBlockId(block.id)
    },
    selectBlock: setSelectedBlockId,
    updateBlock: (id, key, nextValue) => setBlocks((current) => updateBlockTree(
      current,
      id,
      (block) => ({ ...block, values: { ...block.values, [key]: nextValue } }),
    )),
    duplicateBlock: (id) => {
      const source = findBlock(blocks, id)
      if (!source || codingBlockDefinition(source.type).category === 'event') return
      const duplicate = cloneBlock(source)
      setBlocks((current) => insertDuplicate(current, id, duplicate))
      setSelectedBlockId(duplicate.id)
    },
    removeBlock: (id) => {
      const target = findBlock(blocks, id)
      if (!target || target.type === 'when-start') return
      setBlocks((current) => removeBlockTree(current, id).blocks)
      setSelectedBlockId((current) => current === id ? 'coding-start' : current)
    },
    moveBlock: (id, delta) => setBlocks((current) => moveWithinTree(current, id, delta)),
    reorderBlock: (id, targetId) => setBlocks((current) => reorderWithinTree(current, id, targetId)),
    nestBlock: (id, parentId) => {
      const source = findBlock(blocks, id)
      const parent = findBlock(blocks, parentId)
      if (!source || !parent || source.type === 'when-start' || id === parentId) return
      if (!codingBlockDefinition(parent.type).container) return
      if (allCodingBlocks(source.children).some((child) => child.id === parentId)) return
      setBlocks((current) => {
        const result = removeBlockTree(current, id)
        if (!result.removed) return current
        return updateBlockTree(result.blocks, parentId, (target) => ({
          ...target,
          children: [...target.children, result.removed!],
        }))
      })
      setSelectedBlockId(id)
    },
    moveBlockToRoot: (id) => {
      const source = findBlock(blocks, id)
      if (!source || source.type === 'when-start') return
      setBlocks((current) => {
        const result = removeBlockTree(current, id)
        return result.removed ? [...result.blocks, result.removed] : current
      })
      setSelectedBlockId(id)
    },
    clearProgram: () => {
      setBlocks(createDefaultCodingProgram())
      setSelectedBlockId('coding-start')
    },
  }), [blocks, selectedBlockId, setBlocks])

  return <CodingContext.Provider value={value}>{children}</CodingContext.Provider>
}

function useCoding() {
  const value = useContext(CodingContext)
  if (!value) throw new Error('Coding panels must be rendered inside CodingProvider')
  return value
}

function PanelHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return <div className="panel-heading mode-panel-heading"><div><strong>{title}</strong><small>{subtitle}</small></div></div>
}

function CodingSidebar() {
  const { blocks, addBlock } = useCoding()
  const [activeCategory, setActiveCategory] = useState<CodingCategoryId>('motion')
  const [search, setSearch] = useState('')
  const normalizedSearch = search.trim().toLocaleLowerCase('ko')
  const visibleBlocks = CODING_BLOCKS.filter((block) => (
    (!normalizedSearch && block.category === activeCategory)
    || (normalizedSearch && `${block.title} ${block.description}`.toLocaleLowerCase('ko').includes(normalizedSearch))
  ))

  return (
    <aside className="parts-panel mode-sidebar scratch-sidebar">
      <PanelHeading title="블록" subtitle={`${CODING_BLOCKS.length}개`} />
      <label className="search scratch-search">
        <Icon name="search" size={15} />
        <input aria-label="코드 블록 검색" placeholder="블록 검색" value={search} onChange={(event) => setSearch(event.currentTarget.value)} />
      </label>
      <div className="scratch-palette-body">
        <nav className="scratch-category-rail" aria-label="블록 카테고리">
          {CODING_CATEGORIES.map((category) => (
            <button
              className={`${category.id} ${activeCategory === category.id && !normalizedSearch ? 'active' : ''}`}
              aria-pressed={activeCategory === category.id && !normalizedSearch}
              key={category.id}
              onClick={() => { setActiveCategory(category.id); setSearch('') }}
            >
              <i>{category.symbol}</i><span>{category.label}</span>
            </button>
          ))}
        </nav>
        <section className="scratch-palette-list" aria-label="추가할 블록">
          <header><strong>{normalizedSearch ? '검색 결과' : CODING_CATEGORIES.find((item) => item.id === activeCategory)?.label}</strong><span>{visibleBlocks.length}</span></header>
          {visibleBlocks.map((block) => {
            const alreadyAdded = block.type === 'when-start' && allCodingBlocks(blocks).some((item) => item.type === block.type)
            return (
              <button
                className={`scratch-palette-block ${block.category} ${block.container ? 'container' : ''}`}
                draggable={!alreadyAdded}
                key={block.type}
                aria-label={`${block.title} 블록 ${alreadyAdded ? '선택' : '추가'}`}
                onClick={() => addBlock(block.type)}
                onDragStart={(event) => {
                  event.dataTransfer.setData('application/x-pingpong-code-block', block.type)
                  event.dataTransfer.effectAllowed = 'copy'
                }}
              >
                <span>{block.title}</span><b>{alreadyAdded ? '✓' : '+'}</b>
              </button>
            )
          })}
          {!visibleBlocks.length && <p>검색 결과가 없습니다.</p>}
        </section>
      </div>
      <p className="scratch-sidebar-help">클릭하면 맨 아래에 추가됩니다. 반복 블록 안에는 끌어 넣을 수 있습니다.</p>
    </aside>
  )
}

function BlockValue({ block, parameterKey }: { block: CodingProgramBlock; parameterKey: string }) {
  const { updateBlock } = useCoding()
  const definition = codingBlockDefinition(block.type)
  const parameter = definition.parameters.find((item) => item.key === parameterKey)
  if (!parameter) return null
  const value = block.values[parameter.key] ?? parameter.defaultValue
  if (parameter.type === 'select') {
    return (
      <select className="scratch-value select" aria-label={`${definition.title} ${parameter.label}`} value={String(value)} onChange={(event) => updateBlock(block.id, parameter.key, event.currentTarget.value)}>
        {parameter.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    )
  }
  return (
    <input
      className={`scratch-value ${parameter.type}`}
      aria-label={`${definition.title} ${parameter.label}`}
      type={parameter.type}
      min={parameter.min}
      max={parameter.max}
      step={parameter.step}
      value={value}
      onChange={(event) => updateBlock(block.id, parameter.key, parameter.type === 'number' ? event.currentTarget.valueAsNumber : event.currentTarget.value)}
    />
  )
}

function BlockSentence({ block }: { block: CodingProgramBlock }) {
  const value = (key: string) => <BlockValue block={block} parameterKey={key} key={key} />
  const field = (key: string, suffix = '') => <span className="scratch-field" key={key}>{value(key)}{suffix}</span>
  switch (block.type) {
    case 'when-start': return <>⚑ 프로그램이 시작되면</>
    case 'motor-for': return <>{field('seconds', '초간')} {field('cube', '번')} 큐브를 {field('speed', '%')} 속도로 {value('direction')} 회전하기</>
    case 'motor-start': return <>{field('cube', '번')} 큐브를 {field('speed', '%')} 속도로 {value('direction')} 계속 회전하기</>
    case 'motor-stop': return <>{field('cube', '번')} 큐브 모터 멈추기</>
    case 'stop-all': return <>모든 큐브 모터 멈추기</>
    case 'wait': return <>{field('seconds', '초')} 기다리기</>
    case 'repeat': return <>{field('count', '번')} 반복하기</>
    case 'forever': return <>계속 반복하기</>
    case 'if-distance': return <>{field('sensor', '번')} 센서의 거리가 {field('distance', 'cm')}보다 가까우면</>
    case 'read-distance': return <>{field('sensor', '번')} 센서 거리 값 읽기</>
    case 'button-state': return <>{field('cube', '번')} 큐브 버튼 상태 확인하기</>
    case 'set-variable': return <>변수 {value('name')}을 {value('value')}로 정하기</>
    case 'change-variable': return <>변수 {value('name')}에 {value('amount')} 더하기</>
    default: return <>{codingBlockDefinition(block.type).title}</>
  }
}

function ScratchBlockActions({
  block,
  index,
  total,
  parentId,
}: {
  block: CodingProgramBlock
  index: number
  total: number
  parentId?: string
}) {
  const { duplicateBlock, removeBlock, moveBlock, moveBlockToRoot } = useCoding()
  const definition = codingBlockDefinition(block.type)
  const fixed = block.type === 'when-start'
  const minimum = parentId ? 0 : 1
  return (
    <div className="scratch-block-actions">
      <button aria-label={`${definition.title} 위로 이동`} disabled={fixed || index <= minimum} onClick={(event) => { event.stopPropagation(); moveBlock(block.id, -1) }}>↑</button>
      <button aria-label={`${definition.title} 아래로 이동`} disabled={fixed || index === total - 1} onClick={(event) => { event.stopPropagation(); moveBlock(block.id, 1) }}>↓</button>
      {parentId && <button aria-label={`${definition.title} 반복 밖으로 이동`} onClick={(event) => { event.stopPropagation(); moveBlockToRoot(block.id) }}>↗</button>}
      <button aria-label={`${definition.title} 복제`} disabled={fixed} onClick={(event) => { event.stopPropagation(); duplicateBlock(block.id) }}>⧉</button>
      <button className="danger" aria-label={`${definition.title} 삭제`} disabled={fixed} onClick={(event) => { event.stopPropagation(); removeBlock(block.id) }}>×</button>
    </div>
  )
}

function ProgramBlock({
  block,
  index,
  total,
  parentId,
  previewBlockId,
}: {
  block: CodingProgramBlock
  index: number
  total: number
  parentId?: string
  previewBlockId: string | null
}) {
  const { selectedBlockId, selectBlock, addBlock, reorderBlock, nestBlock } = useCoding()
  const definition = codingBlockDefinition(block.type)
  const fixed = block.type === 'when-start'
  const selected = selectedBlockId === block.id
  const commonProps = {
    draggable: !fixed,
    tabIndex: 0,
    onClick: (event: MouseEvent) => { event.stopPropagation(); selectBlock(block.id) },
    onFocus: () => selectBlock(block.id),
    onDragStart: (event: DragEvent<HTMLElement>) => {
      event.stopPropagation()
      event.dataTransfer.setData('application/x-pingpong-program-block', block.id)
      event.dataTransfer.effectAllowed = 'move'
    },
    onDragOver: (event: DragEvent<HTMLElement>) => event.preventDefault(),
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const sourceId = event.dataTransfer.getData('application/x-pingpong-program-block')
      if (sourceId) reorderBlock(sourceId, block.id)
    },
  }

  if (definition.container) {
    return (
      <article
        className={`scratch-block scratch-container ${definition.category} ${selected ? 'selected' : ''} ${previewBlockId === block.id ? 'preview-active' : ''}`}
        aria-label={`${index + 1}번째 블록 ${definition.title}`}
        {...commonProps}
      >
        <div className="scratch-block-main">
          <i className="scratch-grip" aria-hidden="true">⠿</i>
          <strong><BlockSentence block={block} /></strong>
          <ScratchBlockActions block={block} index={index} total={total} parentId={parentId} />
        </div>
        <div
          className="scratch-container-body"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault()
            event.stopPropagation()
            const type = event.dataTransfer.getData('application/x-pingpong-code-block')
            const sourceId = event.dataTransfer.getData('application/x-pingpong-program-block')
            if (type) addBlock(type, block.id)
            else if (sourceId) nestBlock(sourceId, block.id)
          }}
        >
          {block.children.map((child, childIndex) => (
            <ProgramBlock
              block={child}
              index={childIndex}
              total={block.children.length}
              parentId={block.id}
              previewBlockId={previewBlockId}
              key={child.id}
            />
          ))}
          {!block.children.length && <div className="scratch-container-empty"><span>+</span>여기에 블록 끌어 놓기</div>}
        </div>
        <div className="scratch-container-foot"><i /></div>
      </article>
    )
  }

  return (
    <article
      className={`scratch-block ${definition.category} ${fixed ? 'hat' : ''} ${selected ? 'selected' : ''} ${previewBlockId === block.id ? 'preview-active' : ''}`}
      aria-label={`${index + 1}번째 블록 ${definition.title}`}
      {...commonProps}
    >
      <div className="scratch-block-main">
        {!fixed && <i className="scratch-grip" aria-hidden="true">⠿</i>}
        <strong><BlockSentence block={block} /></strong>
        {!fixed && <ScratchBlockActions block={block} index={index} total={total} parentId={parentId} />}
      </div>
    </article>
  )
}

function CodingWorkspace() {
  const { blocks, addBlock, clearProgram } = useCoding()
  const codingProgramName = useStudioStore((state) => state.codingProgramName)
  const setCodingProgramName = useStudioStore((state) => state.setCodingProgramName)
  const [language, setLanguage] = useState<'block' | 'python'>('block')
  const [previewStep, setPreviewStep] = useState<number | null>(null)
  const [previewComplete, setPreviewComplete] = useState(false)
  const [copied, setCopied] = useState(false)
  const previewTimerRef = useRef<number | null>(null)
  const python = codingProgramToPython(blocks)
  const commandCount = codingCommandCount(blocks)
  const previewSequence = useMemo(() => codingProgramPreviewSequence(blocks), [blocks])
  const previewBlockId = previewStep === null ? null : previewSequence[previewStep] ?? null

  useEffect(() => {
    if (previewStep === null) return
    if (previewStep >= previewSequence.length) {
      setPreviewStep(null)
      setPreviewComplete(true)
      previewTimerRef.current = window.setTimeout(() => setPreviewComplete(false), 1300)
      return
    }
    previewTimerRef.current = window.setTimeout(() => setPreviewStep((current) => current === null ? null : current + 1), previewStep === 0 ? 400 : 620)
    return () => {
      if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current)
    }
  }, [previewSequence.length, previewStep])

  const stopPreview = () => {
    if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current)
    setPreviewStep(null)
    setPreviewComplete(false)
  }

  return (
    <section className="code-workspace scratch-workspace">
      <div className="code-toolbar">
        <div className="segmented" aria-label="코드 보기 방식">
          <button className={language === 'block' ? 'active' : ''} onClick={() => setLanguage('block')}>블록</button>
          <button className={language === 'python' ? 'active' : ''} onClick={() => setLanguage('python')}>Python</button>
        </div>
        <span className={`code-status ${previewStep !== null ? 'running' : previewComplete ? 'complete' : ''}`} aria-live="polite">
          <i />{previewStep !== null ? `블록 ${previewStep + 1}/${previewSequence.length}` : previewComplete ? '흐름 확인 완료' : `명령 ${commandCount}개 · 자동 저장`}
        </span>
        <button className="code-clear" disabled={!commandCount} onClick={clearProgram}>모두 지우기</button>
        <button className={`code-run ${previewStep !== null ? 'running' : ''}`} disabled={!commandCount} onClick={() => previewStep !== null ? stopPreview() : setPreviewStep(0)}>
          <Icon name={previewStep !== null ? 'stop' : 'play'} size={15} />{previewStep !== null ? '미리보기 중지' : '흐름 미리보기'}
        </button>
      </div>
      {language === 'block' ? (
        <div
          className="code-canvas scratch-canvas"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault()
            const type = event.dataTransfer.getData('application/x-pingpong-code-block')
            if (type) addBlock(type)
          }}
        >
          <div className="scratch-board">
            <header>
              <label className="scratch-program-name">
                <small>MAIN</small>
                <span>
                  <input
                    aria-label="프로그램 이름"
                    maxLength={30}
                    value={codingProgramName}
                    onChange={(event) => setCodingProgramName(event.target.value)}
                    onBlur={(event) => setCodingProgramName(event.currentTarget.value.trim() || DEFAULT_CODING_PROGRAM_NAME)}
                  />
                  <i aria-hidden="true">✎</i>
                </span>
              </label>
              <p><b>{allCodingBlocks(blocks).length}</b>개 블록</p>
            </header>
            <div className="scratch-program" aria-label="블록 작업공간">
              {blocks.map((block, index) => (
                <ProgramBlock block={block} index={index} total={blocks.length} previewBlockId={previewBlockId} key={block.id} />
              ))}
              <div className="scratch-root-drop"><span>+</span><p><strong>블록 추가</strong><small>클릭하거나 이곳으로 끌어 놓기</small></p></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="python-workspace">
          <header>
            <span><i>PY</i><div><strong>Python 미리보기</strong><small>반복 구조와 블록 값이 자동으로 반영됩니다.</small></div></span>
            <button type="button" onClick={async () => {
              try {
                await navigator.clipboard.writeText(python)
                setCopied(true)
                window.setTimeout(() => setCopied(false), 1200)
              } catch { setCopied(false) }
            }}>{copied ? '복사됨' : '코드 복사'}</button>
          </header>
          <pre aria-label="Python 코드 미리보기"><code>{python}</code></pre>
          <p>현재 코드는 구성 확인용 미리보기이며 실제 큐브 전송은 통신 기능과 함께 연결됩니다.</p>
        </div>
      )}
    </section>
  )
}

function DeviceSidebar() {
  const { blocks } = useCoding()
  const placedParts = useStudioStore((state) => state.placedParts)
  const connections = useStudioStore((state) => state.connections)
  const cubes = orderedCubes(placedParts)
  const connectedPartIds = new Set(connections.flatMap((connection) => [connection.fromPartId, connection.toPartId]))
  const allBlocks = allCodingBlocks(blocks)
  const commands = allBlocks.filter((block) => codingBlockDefinition(block.type).category !== 'event')
  const motorCommands = commands.filter((block) => ['motor-for', 'motor-start', 'motor-stop', 'stop-all'].includes(block.type)).length
  const estimatedSeconds = codingProgramEstimatedSeconds(blocks)

  return (
    <aside className="inspector mode-sidebar coding-device-panel">
      <PanelHeading title="로봇 연결" subtitle="프로그램 대상" />
      <div className="coding-device-summary"><span><small>연결된 큐브</small><strong>{cubes.length}</strong></span><span><small>사용 명령</small><strong>{commands.length}</strong></span></div>
      <section className="coding-device-section">
        <header><strong>큐브 포트</strong><span>{cubes.length ? '조립 순서 기준' : '큐브 없음'}</span></header>
        <div className="coding-device-list">
          {cubes.map((cube, index) => (
            <article key={cube.instanceId}><i>{index + 1}</i><span><strong>{index + 1}번 큐브</strong><small>모터 M{index + 1} · 버튼 B{index + 1}</small></span><b className={connectedPartIds.has(cube.instanceId) ? 'connected' : ''}>{connectedPartIds.has(cube.instanceId) ? '조립됨' : '단독'}</b></article>
          ))}
          {!cubes.length && <div className="coding-device-empty"><Icon name="cube" size={22} /><strong>제어할 큐브가 없습니다</strong><p>조립 탭에서 핑퐁 큐브를 추가하면 번호 순서대로 표시됩니다.</p></div>}
        </div>
      </section>
      <section className="coding-program-check">
        <header><strong>프로그램 확인</strong><span>{commands.length ? '작성 중' : '대기'}</span></header>
        <p className="ok"><i>✓</i><span><b>시작 블록</b><small>프로그램 진입점이 준비되었습니다.</small></span></p>
        <p className={cubes.length || !motorCommands ? 'ok' : 'warning'}><i>{cubes.length || !motorCommands ? '✓' : '!'}</i><span><b>모터 대상</b><small>{motorCommands ? cubes.length ? `${cubes.length}개 큐브에서 모터 명령을 확인합니다.` : '모터 명령을 사용하려면 큐브가 필요합니다.' : '아직 모터 명령이 없습니다.'}</small></span></p>
        <p className={commands.length ? 'ok' : ''}><i>{commands.length ? '✓' : '·'}</i><span><b>실행 흐름</b><small>{commands.length ? `약 ${Math.max(0.1, estimatedSeconds).toFixed(1)}초 · 명령 ${commands.length}개` : '왼쪽에서 첫 명령을 추가하세요.'}</small></span></p>
      </section>
      <div className="coding-device-footer"><i /><span><strong>웹 편집 모드</strong><small>기기 전송 기능은 준비 중입니다.</small></span></div>
    </aside>
  )
}

export function CodingStudioPanels() {
  return <CodingProvider><CodingSidebar /><CodingWorkspace /><DeviceSidebar /></CodingProvider>
}
